import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async"; 
import {
  Shield,
  Lock,
  Eye,
  Cookie,
  Globe,
  Mail,
  Users,
  CheckSquare,
  Scale,
  FileText
} from "lucide-react";
import Aos from "aos";
import "aos/dist/aos.css";
import { useSEO } from "../Pages/Seo";

const PrivacyPolicy = () => {

  useEffect(() => {
    window.scrollTo(0, 0);
    Aos.init({ duration: 500, once: true });
  }, []);

  useSEO({
    title: "Privacy Policy | Game Zone Hub",
    description: "Read the Privacy Policy of Game Zone Hub. Learn how we protect your data, handle cookies, and ensure your safety while you play free online games.",
    keywords: "privacy policy, game zone hub privacy, online games privacy, safe browser games, cookies policy, GDPR, CCPA",
    canonical: "https://www.gamezonehub.in/privacy-policy",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - Game Zone Hub",
    "description": "Privacy Policy and data protection guidelines for Game Zone Hub users.",
    "url": "https://www.gamezonehub.in/privacy-policy",
    "publisher": {
      "@type": "Organization",
      "name": "Game Zone Hub"
    }
  };

  // 🚀 MASSIVELY EXPANDED CONTENT FOR ADSENSE APPROVAL
  const sections = [
    {
      id: "consent",
      title: "1. Consent and Agreement",
      icon: <CheckSquare className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Welcome to <strong>Game Zone Hub</strong>. By accessing and using our website to play free online HTML5 games, you hereby consent to our Privacy Policy and agree to its terms completely. This Privacy document contains types of information that is collected and recorded by Game Zone Hub and how we use it.
          </p>
          <p>
            If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us. This Privacy Policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or collect in Game Zone Hub. This policy is not applicable to any information collected offline or via channels other than this website.
          </p>
        </div>
      ),
    },
    {
      id: "data-collection",
      title: "2. Information We Collect",
      icon: <Eye className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            The personal information that you are asked to provide, and the reasons why you are asked to provide it, will be made clear to you at the point we ask you to provide your personal information. We believe in total transparency regarding your digital footprint while you enjoy our browser games.
          </p>
          <p>
            <strong>Information you provide directly:</strong> If you contact us directly for support regarding a game or website issue, we may receive additional information about you such as your name, email address, phone number, the contents of the message and/or attachments you may send us, and any other information you may choose to provide.
          </p>
          <p>
            <strong>Log Files:</strong> Game Zone Hub follows a standard procedure of using log files. These files log visitors when they visit websites. All hosting companies do this and a part of hosting services' analytics. The information collected by log files includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked to any information that is personally identifiable. The purpose of the information is for analyzing trends, administering the site, tracking users' movement on the website, and gathering demographic information.
          </p>
        </div>
      ),
    },
    {
      id: "how-we-use",
      title: "3. How We Use Your Information",
      icon: <FileText className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>We use the information we collect in various ways to enhance your gaming experience, including to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-blue-500">
            <li>Provide, operate, and maintain our website and gaming portal.</li>
            <li>Improve, personalize, and expand our website to recommend better games.</li>
            <li>Understand and analyze how you use our website and which games are most popular.</li>
            <li>Develop new products, services, features, and gaming functionality.</li>
            <li>Communicate with you, either directly or through one of our partners, for customer service, to provide you with updates and other information relating to the website.</li>
            <li>Find and prevent fraudulent activities or bot traffic.</li>
          </ul>
        </div>
      ),
    },
    {
      id: "adsense",
      title: "4. Cookies, Web Beacons & AdSense",
      icon: <Cookie className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Like any other website, Game Zone Hub uses "cookies". These cookies are used to store information including visitors' preferences, and the pages on the website that the visitor accessed or visited. The information is used to optimize the users' experience by customizing our web page content based on visitors' browser type and/or other information. This helps us keep our massive library of games 100% free for everyone.
          </p>
          <p>
            <strong>Google DoubleClick DART Cookie:</strong> Google is one of a third-party vendor on our site. It also uses cookies, known as DART cookies, to serve ads to our site visitors based upon their visit to www.website.com and other sites on the internet. However, visitors may choose to decline the use of DART cookies by visiting the Google ad and content network Privacy Policy at the following URL – <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer" className="text-blue-400 underline hover:text-blue-300">https://policies.google.com/technologies/ads</a>.
          </p>
          <p>
            Some of advertisers on our site may use cookies and web beacons. Each of our advertising partners has their own Privacy Policy for their policies on user data. We have no access to or control over these cookies that are used by third-party advertisers.
          </p>
        </div>
      ),
    },
    {
      id: "rights",
      title: "5. CCPA & GDPR Privacy Rights",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We would like to make sure you are fully aware of all of your data protection rights. Depending on your location (such as California under CCPA or Europe under GDPR), every user is entitled to the following:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-400 marker:text-cyan-500">
            <li><strong>The right to access</strong> – You have the right to request copies of your personal data. We may charge you a small fee for this service.</li>
            <li><strong>The right to rectification</strong> – You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
            <li><strong>The right to erasure (Right to be forgotten)</strong> – You have the right to request that we erase your personal data, under certain conditions.</li>
            <li><strong>The right to restrict processing</strong> – You have the right to request that we restrict the processing of your personal data, under certain conditions.</li>
            <li><strong>The right to object to processing</strong> – You have the right to object to our processing of your personal data, under certain conditions.</li>
            <li><strong>The right to data portability</strong> – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
          </ul>
          <p className="mt-4 p-4 bg-blue-900/20 border-l-4 border-blue-500 rounded-r-xl text-sm">
            If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please contact us immediately.
          </p>
        </div>
      ),
    },
    {
      id: "security",
      title: "6. Data Security Measures",
      icon: <Lock className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Game Zone Hub takes your digital security very seriously. We have put in place appropriate digital, physical, and managerial security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. 
          </p>
          <p>
            We limit access to your personal data to those employees, agents, contractors, and other third parties who have a strict business need to know. While we strive to use commercially acceptable means (such as SSL encryption) to protect your Personal Information while you play our games, we cannot guarantee its absolute security as no method of transmission over the Internet or method of electronic storage is 100% secure.
          </p>
        </div>
      ),
    },
    {
      id: "children", 
      title: "7. Children's Information (COPPA)",
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            Another part of our core priority is adding protection for children while using the internet. Because we host online games, we strongly encourage parents and guardians to observe, participate in, and/or monitor and guide their children's online activity.
          </p>
          <p>
            <strong>Game Zone Hub</strong> does not knowingly collect any Personal Identifiable Information from children under the age of 13 in strict compliance with the Children's Online Privacy Protection Act (COPPA). If you think that your child provided this kind of information on our website, we strongly encourage you to contact us immediately and we will do our best efforts to promptly remove such information from our records and servers.
          </p>
        </div>
      ),
    },
    {
      id: "contact",
      title: "8. Contact Us",
      icon: <Mail className="w-5 h-5" />,
      content: (
        <div className="space-y-4 text-gray-300 leading-relaxed">
          <p>
            We keep our Privacy Policy under regular review and place any updates on this web page. If you have any questions, concerns, or require more information about our Privacy Policy or how we handle user data while you enjoy our <strong>free browser games</strong>, please do not hesitate to contact our support team.
          </p>
          <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10 inline-block w-full sm:w-auto">
            <p className="flex flex-wrap items-center gap-2">
              <strong className="text-white">Email Address:</strong> 
              <a href="mailto:support@gamezonehub.in" className="text-blue-400 font-semibold hover:text-blue-300 transition-colors">support@gamezonehub.in</a>
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="relative bg-[#0b1120] min-h-screen text-gray-300 sm:pt-[120px] pt-[100px] pb-11">
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center md:mb-20 mb-11">
          <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold mb-3 text-white" data-aos="fade-down">
            Privacy <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Policy</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto" data-aos="fade-up">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
          <p className="text-gray-500 text-sm mt-4 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="100">
             Your privacy is critically important to us. This document outlines the types of personal information that is received and collected by Game Zone Hub and how it is used to provide you with the best gaming experience.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">
          
          <div className="lg:w-1/3">
            <div className="sticky top-28 bg-[#111827]/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-lg" data-aos="fade-right">
              <h3 className="text-white font-bold text-xl mb-4 border-b border-white/10 pb-3">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section, i) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(section.id)?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-center gap-3 px-4 py-3 rounded-xl text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 transition-all"
                  >
                    <span className="text-blue-500 group-hover:scale-110 transition-transform">{section.icon}</span>
                    <span className="text-sm font-medium">{section.title.split(". ")[1]}</span>
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="lg:w-2/3 space-y-8">
            {sections.map((section, i) => (
              <div
                key={section.id}
                id={section.id}
                data-aos="fade-up"
                className="bg-[#111827] rounded-2xl p-6 sm:p-8 border border-white/5 scroll-mt-32 hover:border-blue-500/30 transition-all shadow-xl"
              >
                <div className="flex items-center gap-3 mb-5 border-b border-white/5 pb-4">
                  <div className="p-3 bg-blue-500/10 rounded-xl text-blue-400 shadow-inner">{section.icon}</div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-300 leading-relaxed text-sm sm:text-base">{section.content}</div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;